import { forwardRef } from 'react';

// Stance color mapping: rank goes from 0 (most negative) → highest (most positive)
// Middle ranks are Clarity's Pick (neutral).
const stanceColor = (rank, total) => {
  if (total <= 1) return '#4B5563'; // gray
  const t = rank / (total - 1); // 0..1
  if (t < 0.33) return '#EF4444';   // red — critical
  if (t < 0.66) return '#3B82F6';   // blue — neutral / Clarity's Pick
  return '#10B981';                 // green — supportive
};

const stanceLabel = (rank, total) => {
  if (total <= 1) return 'Single Source';
  const t = rank / (total - 1);
  if (t < 0.33) return 'Critical';
  if (t < 0.66) return 'Neutral';
  return 'Supportive';
};

/**
 * ShareStoryCard — 1200x630 branded card rendering the stance spectrum for a story.
 * Rendered off-screen and converted to PNG via html-to-image.
 */
const ShareStoryCard = forwardRef(({ story }, ref) => {
  const articles = story?.articles || [];
  const total = articles.length;
  // Show up to 5 stances across the spectrum
  const displayArticles = total <= 5
    ? articles
    : [0, Math.floor(total * 0.25), Math.floor(total / 2), Math.floor(total * 0.75), total - 1].map(i => articles[i]);

  return (
    <div
      ref={ref}
      style={{
        width: '1200px',
        height: '630px',
        background: 'linear-gradient(135deg, #0B1220 0%, #111827 45%, #1E293B 100%)',
        fontFamily: '"Playfair Display", Georgia, serif',
        color: '#F8FAFC',
        padding: '56px 64px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Ambient accent */}
      <div style={{
        position: 'absolute',
        top: '-160px',
        right: '-140px',
        width: '520px',
        height: '520px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(59,130,246,0.22) 0%, rgba(59,130,246,0) 70%)',
      }} />
      <div style={{
        position: 'absolute',
        bottom: '-180px',
        left: '-100px',
        width: '460px',
        height: '460px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(239,68,68,0.18) 0%, rgba(239,68,68,0) 70%)',
      }} />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{
            width: '44px', height: '44px', borderRadius: '10px',
            background: '#3B82F6', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: '22px', color: 'white'
          }}>C</div>
          <div style={{ fontFamily: 'Inter, system-ui, sans-serif', letterSpacing: '0.02em' }}>
            <div style={{ fontSize: '22px', fontWeight: 700 }}>Clarity News</div>
            <div style={{ fontSize: '13px', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.14em' }}>Break the Bubble</div>
          </div>
        </div>
        <div style={{
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: '13px',
          padding: '8px 14px',
          border: '1px solid rgba(148,163,184,0.35)',
          borderRadius: '999px',
          color: '#CBD5E1',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}>
          {total} {total === 1 ? 'Source' : 'Sources'} · Stance Spectrum
        </div>
      </div>

      {/* Headline */}
      <div style={{ position: 'relative' }}>
        <h1 style={{
          fontSize: '56px',
          lineHeight: 1.08,
          fontWeight: 700,
          margin: 0,
          letterSpacing: '-0.01em',
          maxHeight: '250px',
          overflow: 'hidden',
        }}>
          {story?.primary_headline || 'A story worth reading from every side'}
        </h1>
      </div>

      {/* Stance Spectrum */}
      <div style={{ position: 'relative' }}>
        {/* Track */}
        <div style={{
          height: '4px',
          borderRadius: '4px',
          background: 'linear-gradient(90deg, #EF4444 0%, #3B82F6 50%, #10B981 100%)',
          opacity: 0.35,
          marginBottom: '28px',
        }} />

        {/* Dots + labels */}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginTop: '-42px' }}>
          {displayArticles.map((a, i) => {
            const rank = a?.stance_rank ?? i;
            const color = stanceColor(rank, Math.max(total, displayArticles.length));
            const isPick = a?.is_clarity_pick;
            return (
              <div key={a?.id || i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0 }}>
                <div style={{
                  width: isPick ? '28px' : '20px',
                  height: isPick ? '28px' : '20px',
                  borderRadius: '50%',
                  background: color,
                  border: isPick ? '4px solid #F8FAFC' : '3px solid #0B1220',
                  boxShadow: isPick ? '0 0 0 4px rgba(59,130,246,0.35)' : 'none',
                  marginBottom: '14px',
                }} />
                <div style={{
                  fontFamily: 'Inter, system-ui, sans-serif',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: '#F8FAFC',
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                }}>
                  {a?.source?.name || 'Source'}
                </div>
                <div style={{
                  fontFamily: 'Inter, system-ui, sans-serif',
                  fontSize: '11px',
                  color: '#94A3B8',
                  textTransform: 'uppercase',
                  letterSpacing: '0.12em',
                  marginTop: '4px',
                }}>
                  {isPick ? "Clarity's Pick" : stanceLabel(rank, Math.max(total, displayArticles.length))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        fontFamily: 'Inter, system-ui, sans-serif',
        position: 'relative',
      }}>
        <div style={{ fontSize: '15px', color: '#94A3B8', maxWidth: '780px', lineHeight: 1.4 }}>
          One story, every angle. See how each newsroom framed it — then decide for yourself.
        </div>
        <div style={{ fontSize: '14px', color: '#CBD5E1', fontWeight: 600 }}>claritynews.app</div>
      </div>
    </div>
  );
});

ShareStoryCard.displayName = 'ShareStoryCard';
export default ShareStoryCard;
